import { useMemo, useState } from 'react';
import { GiftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCharges, useCreateOneTimeDiscount, usePracticeOptions } from '../../hooks/useBilling';
import { useToast } from '../../providers/ToastProvider';
import { apiErrorMessage } from '../../utils/apiError';
import { SearchSelect } from './SearchSelect';
import { chargeNetCents } from './creditAllocation';
import { formatMoneyCents } from '../../utils/money';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Manual one-time discount creation, practice first. Optionally pick the
// source charge (the already-billed charge whose fee is carried forward,
// e.g. a Dx report); picking one prefills the amount with that charge's
// total and the expiry with a standard window, both still editable. The
// one-time discount starts unlinked and gets attached to a future charge
// from the list.

interface CreateOneTimeDiscountModalProps {
    onClose: () => void;
}

// Matches CASE_DESIGN_CREDIT_VALIDITY_DAYS in the backend's caseDesign.ts, so a
// credit raised by hand gets the same window as one the billing rule issues.
const DEFAULT_EXPIRY_DAYS = 90;

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

function dateInputValue(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function CreateOneTimeDiscountModal({ onClose }: CreateOneTimeDiscountModalProps) {
    const { showToast } = useToast();
    const { mutateAsync: create, isPending } = useCreateOneTimeDiscount();

    useEscapeKey(onClose, !isPending);
    const { practices } = usePracticeOptions();

    const [practiceId, setPracticeId] = useState('');
    const [sourceChargeId, setSourceChargeId] = useState('');
    const [amount, setAmount] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [description, setDescription] = useState('');

    // Source candidates: the practice's charges that never charged anybody
    // are out (canceled), and so are charges already carried forward once.
    const { charges } = useCharges({
        page: 1,
        limit: 50,
        practiceId: practiceId || undefined,
        sort: 'date',
        order: 'desc'
    });
    const sourceCandidates = useMemo(
        () =>
            charges.filter(
                (charge) => charge.status !== 'canceled' && charge.issuedOneTimeDiscountsCount === 0
            ),
        [charges]
    );

    const practiceOptions = useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    const chargeOptions = useMemo(
        () => [
            { value: '', label: 'No source charge' },
            ...sourceCandidates.map((charge) => ({
                value: charge.id,
                label: `#${shortRef(charge.id)} · ${charge.productSummary ?? charge.description ?? 'No description'}`,
                sublabel: `${formatMoneyCents(chargeNetCents(charge))} · ${charge.status}`
            }))
        ],
        [sourceCandidates]
    );

    const selectedSource = sourceCandidates.find((charge) => charge.id === sourceChargeId);
    const defaultReason = selectedSource
        ? `One-time discount from ${selectedSource.productSummary ?? selectedSource.description ?? `charge #${shortRef(selectedSource.id)}`}`
        : 'Manual one-time discount';

    const handlePracticeChange = (nextPracticeId: string) => {
        setPracticeId(nextPracticeId);
        setSourceChargeId('');
    };

    // Picking a source charge predefines amount and expiry; both stay editable.
    const handleSourceChange = (nextChargeId: string) => {
        setSourceChargeId(nextChargeId);
        const charge = sourceCandidates.find((c) => c.id === nextChargeId);
        if (charge) {
            setAmount((chargeNetCents(charge) / 100).toFixed(2));
            const expiry = new Date();
            expiry.setDate(expiry.getDate() + DEFAULT_EXPIRY_DAYS);
            setExpiresAt(dateInputValue(expiry));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!practiceId) {
            showToast('Select a practice', 'error');
            return;
        }
        const numericAmount = Number(amount);
        if (amount === '' || isNaN(numericAmount) || numericAmount <= 0) {
            showToast('Enter a valid amount', 'error');
            return;
        }

        try {
            await create({
                amount: numericAmount,
                description: description.trim() || defaultReason,
                expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
                practiceId,
                sourceChargeId: sourceChargeId || undefined
            });
            showToast('One-time discount created', 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to create one-time discount'), 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[520px] max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                    <h2 className="text-lg font-bold text-gray-800">New one-time discount</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-800 transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form
                    onSubmit={(e) => {
                        void handleSubmit(e);
                    }}
                    className="flex flex-col min-h-0 flex-1"
                >
                    <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Practice *
                            </label>
                            <SearchSelect
                                value={practiceId}
                                options={practiceOptions}
                                onChange={handlePracticeChange}
                                placeholder="Select a practice"
                                searchPlaceholder="Search practice or doctor..."
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Source charge
                            </label>
                            <SearchSelect
                                value={sourceChargeId}
                                options={chargeOptions}
                                onChange={handleSourceChange}
                                placeholder={
                                    practiceId ? 'No source charge' : 'Select a practice first'
                                }
                                searchPlaceholder="Search charges..."
                                disabled={!practiceId}
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                                The already-billed charge this one-time discount carries forward,
                                e.g. a Dx report fee.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    Amount *
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        required
                                        className={`${INPUT_CLASS} pl-7`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    Expires
                                </label>
                                <input
                                    type="date"
                                    value={expiresAt}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                    className={INPUT_CLASS}
                                />
                                <p className="mt-1 text-[11px] text-gray-400">
                                    Leave empty for no expiry.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Reason
                            </label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={defaultReason}
                                className={INPUT_CLASS}
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                                Optional; the placeholder is used when left empty.
                            </p>
                        </div>

                        <div className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                            <GiftIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-gray-500 leading-relaxed">
                                Picking a source charge prefills the amount and a{' '}
                                {DEFAULT_EXPIRY_DAYS}-day expiry; both stay editable. The one-time
                                discount starts unlinked and can be attached to an open charge from
                                the list.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isPending}
                            className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isPending ? 'Creating…' : 'Create one-time discount'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
